import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { View, StyleSheet, ListRenderItem } from 'react-native';
import { FlashList, FlashListRef } from '@shopify/flash-list';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { NC_FILE_ESTIMATED_ROW_HEIGHT } from '@v2/pages/machines/modules/now/components/Overview/constants';
import Loading from '@components/loading';
import ScrollToTopButton from '@components/ncFileVersions/components/NCFileDiffView/ScrollToTopButton';
import NCLine from './NCLine';
import NCToolCallHeader from './NCToolCallHeader';
import { useManageNCTextVirtualized } from '@components/common/NCFile/hooks/useManageNCTextVirtualized';
import { NCListItem, NCListItemType, NCTextVirtualizedProps, NCTextVirtualizedRef } from '@components/common/NCFile/types';

const NCTextVirtualized = forwardRef<NCTextVirtualizedRef, NCTextVirtualizedProps>(
  (
    {
      data,
      C,
      backgroundColor,
      initialCollapsed,
      paddingHorizontal,
      scale,
      paddingTop,
      ncFileTextColor,
      singleColor,
      borderTop,
      borderRadiusTop,
      borderRadiusBottom,
      withShadow,
      containerStyle,
      withoutCollapsingFeatureAndStyles = false,
      ncFileContentFontConfig,
    },
    ref
  ) => {
    const flashListRef = useRef<FlashListRef<NCListItem>>(null);

    const { isProcessing, preparedListItems, expandedSections, showScrollTop, toggleSection, collapseAll, expandAll, handleScroll } =
      useManageNCTextVirtualized({
        data,
        initialCollapsed,
        withoutCollapsingFeatureAndStyles,
      });

    const scrollToTop = useCallback(() => {
      flashListRef.current?.scrollToOffset({ offset: 0, animated: true });
    }, []);

    useImperativeHandle(ref, () => ({
      collapseAll,
      expandAll,
      scrollToTop,
    }));

    const renderItem: ListRenderItem<NCListItem> = useCallback(
      ({ item }) => {
        if (item.type === NCListItemType.TOOL_CALL_HEADER) {
          return (
            <NCToolCallHeader
              C={C}
              line={item.line}
              paddingHorizontal={paddingHorizontal}
              scale={scale}
              ncFileTextColor={ncFileTextColor}
              singleColor={singleColor}
              ncFileContentFontConfig={ncFileContentFontConfig}
              isExpanded={expandedSections.has(item.sectionIndex)}
              onPress={() => toggleSection(item.sectionIndex)}
            />
          );
        }

        return (
          <NCLine
            C={C}
            line={item.line}
            paddingHorizontal={paddingHorizontal}
            scale={scale}
            ncFileTextColor={ncFileTextColor}
            singleColor={singleColor}
            ncFileContentFontConfig={ncFileContentFontConfig}
          />
        );
      },
      [C, paddingHorizontal, scale, ncFileTextColor, singleColor, ncFileContentFontConfig, expandedSections, toggleSection]
    );

    const keyExtractor = useCallback((item: NCListItem) => item.key, []);

    return (
      <View
        style={[
          styles.container,
          borderTop && { borderTopColor: C('Hairline/Light/Primary'), borderTopWidth: StyleSheet.hairlineWidth },
          withShadow && getBoxShadowStyle({ type: 'Field', C }),
          { backgroundColor },
          borderRadiusTop && { borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS), borderTopRightRadius: scale(SCREEN_BORDER_RADIUS) },
          borderRadiusBottom && { borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS), borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS) },
          containerStyle,
        ]}
      >
        {isProcessing ? (
          <View style={styles.loadingContainer}>
            <Loading background={{ backgroundColor }} />
          </View>
        ) : (
          <FlashList
            ref={flashListRef}
            data={preparedListItems}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            estimatedItemSize={NC_FILE_ESTIMATED_ROW_HEIGHT}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            contentContainerStyle={{ paddingTop: paddingTop ?? 0, paddingBottom: scale(50) }}
          />
        )}
        <ScrollToTopButton C={C} visible={showScrollTop} onPress={scrollToTop} />
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default NCTextVirtualized;
